/**
 * AllSessionsModal — the daemon-wide PTY session list (orphan surface).
 *
 * The per-project terminal tabs only ever show sessions matched to THEIR
 * project, so a session whose project link can't be resolved (moved folder,
 * pre-stamping spawn, deleted project) is invisible everywhere and burns a
 * daemon slot forever. This modal lists EVERY live session with its
 * reconstitution stamps (project, kind, agent, identity, age) and offers:
 *  - **Adopt** — current-project sessions with no tab become a tab again.
 *  - **Close** — kill any session (the explicit-close path; confirm via
 *    double-click semantics: first click arms, second confirms).
 *
 * Data comes straight from backend.terminal.listSessions() on open + manual
 * refresh — no live subscription; this is a maintenance surface, not a
 * monitor.
 */
import { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { X, RefreshCw, Bot, Play, KeyRound, TerminalSquare, Plug, Trash2 } from 'lucide-react';
import { useBackend } from '@/services/backend';
import type { TerminalSessionData } from '@/services/backend/IBackend';
import { useTerminalStore } from '@/stores/terminalStore';
import { useProjectStore } from '@/stores/projectStore';
import { logger } from '@/lib/logger';

const TAG = 'AllSessionsModal';

function age(createdAt?: string): string {
  if (!createdAt) return '—';
  const ms = Date.now() - new Date(createdAt).getTime();
  if (!Number.isFinite(ms) || ms < 0) return '—';
  const m = Math.floor(ms / 60_000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h${m % 60 ? ` ${m % 60}m` : ''}`;
  return `${Math.floor(h / 24)}d`;
}

const KIND_ICON: Record<string, typeof Bot> = {
  agent: Bot,
  run: Play,
  auth: KeyRound,
  terminal: TerminalSquare,
};

export default function AllSessionsModal({
  currentProjectPath,
  onClose,
}: {
  /** safePath of the project whose TerminalTab opened the modal — sessions
   *  matching it (and not already tabbed) get the Adopt action. */
  currentProjectPath: string;
  onClose: () => void;
}) {
  const backend = useBackend();
  const projects = useProjectStore((s) => s.projects);
  const [sessions, setSessions] = useState<TerminalSessionData[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  /** Session id armed for close-confirmation (second click kills). */
  const [armedClose, setArmedClose] = useState<number | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      setSessions(await backend.terminal.listSessions());
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
      setSessions([]);
    }
  }, [backend]);

  useEffect(() => { void load(); }, [load]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  /** Resolve a session to a known project (stamp id first, cwd fallback). */
  const projectOf = useCallback((s: TerminalSessionData) => {
    if (s.projectId) {
      const byId = projects.find((p) => p.id === s.projectId);
      if (byId) return byId;
    }
    return projects.find((p) => p.root_path === s.projectPath) ?? null;
  }, [projects]);

  const adopt = useCallback((s: TerminalSessionData) => {
    const store = useTerminalStore.getState();
    store.addSession(s.id, s.title, currentProjectPath, s.isAgent ?? false);
    if (s.kind === 'run' && store.runSessionIds.get(currentProjectPath) == null) {
      store.setRunSession(currentProjectPath, s.id);
    }
    store.setActiveTerminal(currentProjectPath, s.id);
    onClose();
  }, [currentProjectPath, onClose]);

  const close = useCallback(async (s: TerminalSessionData) => {
    if (armedClose !== s.id) {
      setArmedClose(s.id);
      return;
    }
    setArmedClose(null);
    try {
      await backend.terminal.close(s.id);
    } catch (e) {
      logger.warn(TAG, `close ${s.id} failed`, e);
    }
    useTerminalStore.getState().removeSession(s.id);
    await load();
  }, [armedClose, backend, load]);

  const tabbedIds = new Set(
    useTerminalStore.getState().getProjectSessions(currentProjectPath).map((t) => t.id),
  );

  return createPortal(
    <div className="fixed inset-0 z-[9000] flex items-center justify-center p-6">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative w-full max-w-2xl max-h-[80vh] bg-base-100 border border-base-300 rounded-xl shadow-2xl flex flex-col overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-base-300 shrink-0">
          <div>
            <div className="text-sm font-medium">All terminal sessions</div>
            <div className="text-xs text-base-content/50">
              Every live PTY on the daemon, across all projects — including orphans no tab can see.
            </div>
          </div>
          <div className="flex items-center gap-1">
            <button type="button" className="btn btn-ghost btn-sm btn-square" onClick={() => void load()} title="Refresh">
              <RefreshCw className="w-4 h-4" />
            </button>
            <button type="button" className="btn btn-ghost btn-sm btn-square" onClick={onClose}>
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-2">
          {error && <div className="alert alert-warning text-xs py-2 mb-2">{error}</div>}
          {sessions === null ? (
            <div className="flex justify-center py-8"><span className="loading loading-spinner loading-sm" /></div>
          ) : sessions.length === 0 ? (
            <div className="text-center text-sm text-base-content/40 py-8">No live sessions.</div>
          ) : (
            sessions.map((s) => {
              const proj = projectOf(s);
              const orphan = !proj;
              const isCurrent = proj?.root_path === currentProjectPath || s.projectPath === currentProjectPath;
              const adoptable = isCurrent && !tabbedIds.has(s.id);
              const KindIcon = KIND_ICON[s.kind ?? 'terminal'] ?? TerminalSquare;
              return (
                <div
                  key={s.id}
                  className={`flex items-center gap-2.5 px-2.5 py-2 rounded-md text-xs ${
                    orphan ? 'bg-warning/5 border border-warning/20' : 'hover:bg-base-200'
                  }`}
                >
                  <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${s.attached ? 'bg-success' : 'bg-base-content/20'}`}
                    title={s.attached ? 'a UI is attached' : 'detached (running unattended)'} />
                  <span className="font-mono text-[10px] text-base-content/40 w-6 text-right shrink-0">{s.id}</span>
                  <KindIcon className="w-3.5 h-3.5 text-base-content/50 shrink-0" />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5">
                      <span className="truncate font-medium">{s.title}</span>
                      {s.agentType && <span className="badge badge-xs badge-primary badge-outline">{s.agentType}</span>}
                      {s.runConfig && <span className="badge badge-xs badge-ghost">{s.runConfig}</span>}
                      {s.claudeIdentity && (
                        <span className="text-[9px] text-base-content/40">· {s.claudeIdentity}</span>
                      )}
                    </div>
                    <div className="text-[10px] text-base-content/45 truncate">
                      {proj ? proj.name : <span className="text-warning">unknown project</span>}
                      <span className="opacity-60"> · {s.projectPath}</span>
                    </div>
                  </div>
                  <span className="text-[10px] font-mono text-base-content/40 shrink-0" title={s.createdAt}>{age(s.createdAt)}</span>
                  {adoptable && (
                    <button type="button" className="btn btn-ghost btn-xs gap-1 text-primary" onClick={() => adopt(s)}
                      title="Re-open this session as a tab in the current project">
                      <Plug className="w-3 h-3" /> Adopt
                    </button>
                  )}
                  <button
                    type="button"
                    className={`btn btn-xs gap-1 ${armedClose === s.id ? 'btn-error' : 'btn-ghost text-error/70'}`}
                    onClick={() => void close(s)}
                    onMouseLeave={() => armedClose === s.id && setArmedClose(null)}
                    title={armedClose === s.id ? 'Click again to kill this session' : 'Close this session (kills its process)'}
                  >
                    <Trash2 className="w-3 h-3" />
                    {armedClose === s.id ? 'Sure?' : 'Close'}
                  </button>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
